import {
  LayoutDashboard,
  Users,
  Server,
  Globe,
  Receipt,
  LifeBuoy,
  BarChart3,
  Settings,
  Package,
  Activity,
  Wallet,
} from 'lucide-react';
import type { TopNavItem, SubNavItem } from '@sighthost/ui';

/**
 * AdminNavigation — the admin console's navigation configuration.
 *
 * Mirrors `ClientNavigation.tsx`, which is the reference implementation for
 * two-level navigation in this codebase (CLAUDE.md §4.1, ARCHITECTURE_FRAMEWORK
 * §3.5): primary nav in `UniversalTopNav`, section nav in `UniversalSubNav`,
 * resolved from the pathname by the layout wrapper.
 *
 * Why this file exists (AR-7, ARCHITECTURE_RULING_ADMIN_NAVIGATION_2026-08-30):
 * admin previously had NO second level. `UniversalSubNav` appeared exactly once
 * in the whole portal — in the client app — and the four admin sections that
 * needed a section nav each hand-rolled their own `<nav>` inside a route layout
 * component instead.
 *
 * That duplication was not merely untidy, it was CAUSAL. Adding a section
 * should cost one array entry here; while the four copies existed it cost
 * hand-writing a fifth bespoke nav with its own Tailwind, active-state logic
 * and a11y label. So when Ops, Care and Services arrived, the cheap correct
 * action did not exist and the expensive one was skipped — the routes shipped
 * with no navigation at all. Eleven pages ended up unreachable from the nav.
 *
 * PATHS ARE ABSOLUTE, deliberately. The hand-rolled navs used route-relative
 * paths ('.', 'pending-orders') which resolved correctly because they rendered
 * INSIDE the section's route. `UniversalSubNav` renders in the app shell, whose
 * route is pathless, so a relative path would resolve against /admin and land
 * on /admin/pending-orders. Every `to` here is absolute for that reason.
 */

// `end: true` on '/admin' is load-bearing: it is a prefix of every other route
// in this console, so without it NavLink lights Dashboard up on every page.
export const adminPrimaryNav: TopNavItem[] = [
  { label: 'Dashboard', to: '/admin', end: true, icon: LayoutDashboard },
  { label: 'Customers', to: '/admin/customers', icon: Users },
  { label: 'Hosting',   to: '/admin/hosting',   icon: Server },
  { label: 'Domains',   to: '/admin/domains',   icon: Globe },
  { label: 'Billing',   to: '/admin/billing',   icon: Receipt },
  { label: 'Support',   to: '/admin/support',   icon: LifeBuoy },
  { label: 'Services',  to: '/admin/services',  icon: Package },
  { label: 'Ops',       to: '/admin/ops',      icon: Activity },
  { label: 'Financials', to: '/admin/financials', icon: Wallet },
  { label: 'Reports',   to: '/admin/reports',   icon: BarChart3 },
  { label: 'Settings',  to: '/admin/settings',  icon: Settings },
];

// Support agents (2026-07-06 re-scope) do full per-client operations but must
// not see SightHost-confidential financials — so their nav omits Reports,
// Settings, and Sales. The route guard + RLS enforce the same boundary.
export const supportNav: TopNavItem[] = [
  { label: 'Dashboard', to: '/admin', end: true, icon: LayoutDashboard },
  { label: 'Customers', to: '/admin/customers', icon: Users },
  { label: 'Hosting',   to: '/admin/hosting',   icon: Server },
  { label: 'Domains',   to: '/admin/domains',   icon: Globe },
  { label: 'Billing',   to: '/admin/billing',   icon: Receipt },
  { label: 'Support',   to: '/admin/support',   icon: LifeBuoy },
  // Services yes, Ops NO. The audit log under /admin/ops is is_full_staff only,
  // and offering a door that RLS closes is worse than offering none.
  { label: 'Services',  to: '/admin/services',  icon: Package },
];

/* Section sub-navs. `end: true` marks the section's index so it deactivates
 * when a child route is active — without it the index entry stays lit
 * alongside the child, and the active state stops meaning anything. */

export const adminHostingSubNav: SubNavItem[] = [
  { label: 'Accounts',         to: '/admin/hosting', end: true },
  { label: 'Server health',    to: '/admin/hosting/server-health' },
  { label: 'Termination queue', to: '/admin/hosting/terminations' },
];

export const adminBillingSubNav: SubNavItem[] = [
  { label: 'Invoices',          to: '/admin/billing', end: true },
  { label: 'Pending orders',    to: '/admin/billing/pending-orders' },
  { label: 'Proofs of payment', to: '/admin/billing/proofs' },
  { label: 'Payments',          to: '/admin/billing/payments' },
  { label: 'Credit notes',      to: '/admin/billing/credit-notes' },
  { label: 'Dunning',           to: '/admin/billing/dunning' },
  { label: 'Recoupment',        to: '/admin/billing/recoupment' },
];

// C1: `domains/expiring` and `domains/transfers` were linked from NOWHERE in
// the admin app before 2026-08-30 — two orphans the original diagnostic missed.
// `end` on the index matters here: /admin/domains/:domain is a detail route and
// should light the section, not the "All domains" tab.
export const adminDomainsSubNav: SubNavItem[] = [
  { label: 'All domains',    to: '/admin/domains', end: true },
  { label: 'Expiring',       to: '/admin/domains/expiring' },
  { label: 'Transfers',      to: '/admin/domains/transfers' },
  { label: 'Reconciliation', to: '/admin/domains/reconcile' },
];

export const adminServicesSubNav: SubNavItem[] = [
  { label: 'Orders',              to: '/admin/services', end: true },
  { label: 'Care subscriptions',  to: '/admin/care' },
  { label: 'Instalment recovery', to: '/admin/services/installment-recovery' },
];

// Six pages that had no front door at all. Each already rendered a breadcrumb
// reading { label: 'Ops', href: '/admin/ops/audit' } — the section knew it was
// a section and named itself, and that name appeared nowhere in the navigation.
export const adminFinancialsSubNav: SubNavItem[] = [
  { label: 'Monthly statement', to: '/admin/financials', end: true },
  { label: '12-month report',   to: '/admin/financials/12-month' },
  { label: 'Yearly report',     to: '/admin/financials/yearly' },
  { label: 'Projections',       to: '/admin/financials/projections' },
  { label: 'Expenses ledger',   to: '/admin/financials/expenses' },
];

export const adminOpsSubNav: SubNavItem[] = [
  { label: 'Action queue', to: '/admin/ops/action-queue' },
  { label: 'Provisioning', to: '/admin/ops/provisioning' },
  { label: 'Cron health',  to: '/admin/ops/cron-health' },
  { label: 'Notifications', to: '/admin/ops/notifications' },
  { label: 'Audit log',    to: '/admin/ops/audit' },
  { label: 'Bulk actions', to: '/admin/ops/bulk-actions' },
];

// Seven entries. Was eight, with a Financials tab that opened a THIRD
// navigation level — the only genuine §4.1 violation in the console. Moving
// money reporting to its own section (AR-11) lands Reports inside the 3-7
// guidance for the first time, which is the tell that the split was real and
// not arithmetic: Reports was doing two jobs.
export const adminReportsSubNav: SubNavItem[] = [
  { label: 'Overview',    to: '/admin/reports', end: true },
  { label: 'Lifecycle',   to: '/admin/reports/lifecycle' },
  { label: 'Provisioning', to: '/admin/reports/provisioning' },
  { label: 'Support SLA', to: '/admin/reports/support' },
  { label: 'Domains',     to: '/admin/reports/domains' },
  { label: 'Services',    to: '/admin/reports/services' },
  { label: 'Acquisition', to: '/admin/reports/acquisition' },
];

export const adminSettingsSubNav: SubNavItem[] = [
  { label: 'System',          to: '/admin/settings', end: true },
  { label: 'Hosting plans',   to: '/admin/settings/plans' },
  { label: 'Staff',           to: '/admin/settings/staff' },
  { label: 'Email templates', to: '/admin/settings/email-templates' },
  { label: 'Announcements',   to: '/admin/settings/announcements' },
];

/**
 * Pathname prefix -> section nav. Resolution is first-match-wins
 * (`.find(s => pathname.startsWith(s.prefix))`), so ORDER MATTERS: a longer,
 * more specific prefix must appear before any shorter prefix that would
 * shadow it. There is no such collision today; the constraint is recorded so
 * the next person adding a section does not create one.
 */
export const adminSubNavByPrefix: Array<{ prefix: string; items: SubNavItem[] }> = [
  { prefix: '/admin/hosting',  items: adminHostingSubNav  },
  { prefix: '/admin/domains',  items: adminDomainsSubNav  },
  { prefix: '/admin/billing',  items: adminBillingSubNav  },
  { prefix: '/admin/services', items: adminServicesSubNav },
  // /admin/care is part of the Services SECTION but not of its PATH, so the
  // startsWith match needs its own entry or the section nav would vanish the
  // moment a reader opened Care subscriptions. Mapping it here costs one line;
  // moving the route to /admin/services/care would be a URL change and
  // deserves its own decision.
  { prefix: '/admin/care',     items: adminServicesSubNav },
  { prefix: '/admin/ops',      items: adminOpsSubNav      },
  { prefix: '/admin/financials', items: adminFinancialsSubNav },
  { prefix: '/admin/reports',  items: adminReportsSubNav  },
  { prefix: '/admin/settings', items: adminSettingsSubNav },
];
